<template>
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Shipping List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Shipping
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="shippings"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-info me-2" @click="viewShipping(item as Shipping)">
                            <i class="fa fa-eye"></i>
                        </button>
                        <button class="btn btn-sm btn-warning me-2" @click="editShipping(item as Shipping)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteShipping(item as Shipping)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="shippingModal" tabindex="-1" ref="modalRef" aria-labelledby="shippingModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Code</label>
                                <input v-model="form.code" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                        </div>
                        <hr>
                        <div class="mb-2">
                            <label class="form-label col-form-label">Couriers</label>
                            <div v-for="(courier, idx) in form.couriers" :key="courier.ID ?? idx" class="border rounded p-2 mb-2">
                                <div class="row g-2">
                                    <div class="col-md-4">
                                        <input v-model="courier.code" type="text" class="form-control" placeholder="Courier Number" required>
                                    </div>
                                    <div class="col-md-4">
                                        <input v-model="courier.name" type="text" class="form-control" placeholder="Courier Name" required>
                                    </div>
                                    <div class="col-md-3">
                                        <input v-model="courier.desc" type="text" class="form-control" placeholder="Cabang" required>
                                    </div>
                                    <div class="col-md-1 d-flex align-items-center">
                                        <button type="button" class="btn btn-danger btn-sm" @click="removeCourier(idx)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button type="button" class="btn btn-success btn-sm mt-2" @click="addCourier">
                            <i class="fa fa-plus"></i> Add Courier
                        </button>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="saveShipping">Save</button>
                    </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="viewShippingModal" tabindex="-1" ref="viewModalRef">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Shipping & Courier Information</h5>
                    <button type="button" class="btn-close" @click="closeViewModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="selectedShipping">
                        <div class="mb-2"><strong>Code:</strong> {{ selectedShipping.code }}</div>
                        <div class="mb-2"><strong>Shipping:</strong> {{ selectedShipping.name }}</div>
                        <div>
                            <strong>Couriers:</strong>
                            <ul v-if="selectedShipping.couriers && selectedShipping.couriers.length">
                                <li v-for="(courier, idx) in selectedShipping.couriers" :key="courier.ID ?? idx">
                                    <span>
                                        <strong>Code:</strong> {{ courier.code }},
                                        <strong>Nama:</strong> {{ courier.name }},
                                        <strong>Desc:</strong> {{ courier.desc }}
                                    </span>
                                </li>
                            </ul>
                            <div v-else>
                                <em>No couriers available.</em>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeViewModal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

const shippings = ref<Shipping[]>([])
const loading = ref(false)
const total = ref(0)
const modalTitle = ref('')
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const headers = [
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Actions', value: 'actions' }
]

const initForm = {
    ID: null,
    code: '',
    name: '',
    couriers: [] as {
        ID: number | null,
        code: string,
        name: string,
        desc: string
    }[]
}

const form = reactive({
    ...initForm
})

const dataShippings: Shipping[] = []
import * as master from "@/api/services/MasterRequest";

const fetchShippings = async () => {
    loading.value = true
    try {
        const resp = await master.postRequest("/shipping/list", {...params});
        dataShippings.splice(0, dataShippings.length);
        dataShippings.push(...resp.data);

        let filteredShippings = [...dataShippings]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredShippings = dataShippings.filter(shipping =>
                shipping.code.toLowerCase().includes(searchLower) ||
                shipping.name.toLowerCase().includes(searchLower)
            )
        }

        const start = (params.page - 1) * params.size
        const end = start + params.size
        shippings.value = filteredShippings.slice(start, end)
        total.value = filteredShippings.length

    } catch (error) {
        toast.error('Failed to fetch shippings')
    }
    loading.value = false
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchShippings()
}

const modalRef = ref<HTMLElement | null>(null)
let bsModal: any | null = null

const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedShipping = ref<Shipping | null>(null)

onMounted(() => {
    if (modalRef.value) {
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
})

const closeModal = () => {
    if (bsModal) {
        bsModal.hide()
    }
}

const editShipping = (shipping: Shipping) => {
    openModal(shipping);
}

const saveShipping = async () => {
    loading.value = true
    try {
        if (form.ID) {
            await master.postRequest("/shipping/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Shipping updated successfully');
                    closeModal();
                    fetchShippings();
                } else {
                    toast.error('Failed to update shipping');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update shipping');
            })
        } else {
            await master.postRequest("/shipping/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Shipping created successfully');
                    closeModal();
                    fetchShippings();
                } else {
                    toast.error('Failed to create shipping');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create shipping');
            })
        }
    } catch (error: any) {
        toast.error(error.message || 'Failed to save shipping');
    } finally {
        loading.value = false
    }
}

const deleteShipping = async (shipping: Shipping) => {
    if (!confirm('Are you sure you want to delete this shipping?')) return

    loading.value = true
    try {
        await master.postRequest("/shipping/delete", {ID: shipping.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Shipping deleted successfully');
                closeModal();
                fetchShippings();
            } else {
                toast.error('Failed to delete shipping');
            }
        })

    } catch (error: any) {
        toast.error(error.message || 'Failed to delete shipping');
    } finally {
        loading.value = false
    }
}

const addCourier = () => {
    form.couriers = form.couriers || [];
    form.couriers.push({
        ID: null,
        code: '',
        name: '',
        desc: ''
    });
};

const removeCourier = (idx: number) => {
    if (form.couriers) form.couriers.splice(idx, 1);
};

const openModal = (shipping: Shipping | null = null) => {
    if (shipping) {
        modalTitle.value = 'Edit Shipping'
        Object.assign(form, shipping);
        form.couriers = shipping.couriers ? shipping.couriers.map(acc => ({ ...acc })) : [];
    } else {
        modalTitle.value = 'Create Shipping'
        Object.assign(form, {...initForm})
        console.log(form)
        console.log(initForm)
    }
    if (bsModal) {
        bsModal.show()
    }
}

const viewShipping = (shipping: Shipping) => {
    selectedShipping.value = shipping
    if (!bsViewModal && viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
    if (bsViewModal) {
        bsViewModal.show()
    }
}

const closeViewModal = () => {
    if (bsViewModal) {
        bsViewModal.hide()
    }
    selectedShipping.value = null
}
</script>